import { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../home/components/Footer';
import { mockDramas, dramaGenres, dramaCountries, dramaStatuses } from '../../mocks/dramas';
import { createDramaUrl } from '../../hooks/useNavigation';
import AIDramasGallery from './components/AIDramasGallery';
import AIDramaApplyModal from '../../components/base/AIDramaApplyModal';

const DramasPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [sortBy, setSortBy] = useState('latest');
  const [showAIDramaApply, setShowAIDramaApply] = useState(false);
  
  const location = useLocation();
  const aiGalleryRef = useRef<HTMLDivElement>(null);
  const [highlightGallery, setHighlightGallery] = useState(false);

  // Handle query parameter for AI Dramas Gallery
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has('aidramasgallery') || location.search === '?aidramasgallery') {
      // Small delay to ensure the component is rendered
      setTimeout(() => {
        if (aiGalleryRef.current) {
          aiGalleryRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          setHighlightGallery(true);
          // Remove highlight after animation
          setTimeout(() => setHighlightGallery(false), 2000);
        }
      }, 100);
    }
  }, [location.search]);

  const filteredDramas = useMemo(() => {
    let filtered = [...mockDramas];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(drama =>
        drama.title.toLowerCase().includes(query) ||
        drama.description.toLowerCase().includes(query) ||
        drama.cast.some(actor => actor.toLowerCase().includes(query)) ||
        drama.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }

    if (selectedGenre !== 'all') {
      filtered = filtered.filter(drama =>
        drama.genre.some(g => g.toLowerCase() === selectedGenre.toLowerCase())
      );
    }

    if (selectedStatus !== 'all') {
      filtered = filtered.filter(drama => drama.status === selectedStatus);
    }

    if (selectedCountry !== 'all') {
      filtered = filtered.filter(drama => drama.country === selectedCountry);
    }

    switch (sortBy) {
      case 'latest':
        filtered.sort((a, b) => new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime());
        break;
      case 'rating':
        filtered.sort((a, b) => b.rating - a.rating);
        break;
      case 'popular':
        filtered.sort((a, b) => parseFloat(b.views.replace(/[^\d.]/g, '')) - parseFloat(a.views.replace(/[^\d.]/g, '')));
        break;
      case 'alphabetical':
        filtered.sort((a, b) => a.title.localeCompare(b.title));
        break;
    }

    return filtered;
  }, [searchQuery, selectedGenre, selectedStatus, selectedCountry, sortBy]);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedGenre('all');
    setSelectedStatus('all');
    setSelectedCountry('all');
    setSortBy('latest');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-rose-900 via-rose-800 to-amber-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Arab Short Dramas</h1>
            <p className="text-xl text-rose-200 mb-8 max-w-3xl mx-auto">
              Discover captivating stories from across the Arab world. From modern romances to historical epics, 
              explore the rich tapestry of Middle Eastern storytelling.
            </p>
            
            {/* Search Bar */}
            <div className="max-w-2xl mx-auto relative">
              <input
                type="text"
                placeholder="Search dramas, actors, or genres..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-6 py-4 text-lg text-gray-900 bg-white rounded-full shadow-lg focus:outline-none focus:ring-4 focus:ring-rose-300 pr-14"
              />
              <button className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-rose-600 hover:bg-rose-700 text-white p-3 rounded-full transition-colors cursor-pointer">
                <i className="ri-search-line text-lg"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Genre</label>
              <select
                value={selectedGenre}
                onChange={(e) => setSelectedGenre(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 pr-8"
              >
                <option value="all">All Genres</option>
                {dramaGenres.map(genre => (
                  <option key={genre} value={genre}>{genre}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 pr-8"
              >
                <option value="all">All Status</option>
                {dramaStatuses.map(status => (
                  <option key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
              <select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 pr-8"
              >
                <option value="all">All Countries</option>
                {dramaCountries.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 pr-8"
              >
                <option value="latest">Latest</option>
                <option value="rating">Highest Rated</option>
                <option value="popular">Most Popular</option>
                <option value="alphabetical">A-Z</option>
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-gray-600">
            Showing {filteredDramas.length} of {mockDramas.length} dramas
          </p>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">View:</span>
            <button className="p-2 bg-rose-100 text-rose-600 rounded-lg cursor-pointer">
              <i className="ri-grid-line"></i>
            </button>
            <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg cursor-pointer">
              <i className="ri-list-unordered"></i>
            </button>
          </div>
        </div>

        {/* Drama Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-product-shop>
          {filteredDramas.map((drama) => (
            <Link
              key={drama.id}
              to={createDramaUrl(drama.genreSlug, drama.slug)}
              className="group block bg-white rounded-lg shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden"
            >
              <div className="relative overflow-hidden">
                <img
                  src={drama.poster}
                  alt={drama.title}
                  className="w-full h-80 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-3 right-3 bg-black/70 text-white px-2 py-1 rounded text-xs font-medium">
                  <i className="ri-star-fill text-yellow-400 mr-1"></i>
                  {drama.rating}
                </div>
                <div className="absolute top-3 left-3">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    drama.status === 'ongoing' ? 'bg-green-500 text-white' :
                    drama.status === 'completed' ? 'bg-rose-500 text-white' :
                    'bg-orange-500 text-white'
                  }`}>
                    {drama.status.charAt(0).toUpperCase() + drama.status.slice(1)}
                  </span>
                </div>
                <div className="absolute bottom-3 left-3 bg-black/70 text-white px-2 py-1 rounded text-xs">
                  {drama.episodes} episodes
                </div>
              </div>
              
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 group-hover:text-rose-700 transition-colors mb-2 line-clamp-1">
                  {drama.title}
                </h3>
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                  {drama.description}
                </p>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{drama.year} • {drama.country}</span>
                    <span className="flex items-center">
                      <i className="ri-eye-line mr-1"></i>
                      {drama.views}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-rose-600 font-medium">{drama.genre.join(', ')}</span>
                    <span className="text-xs text-gray-500">{drama.duration}</span>
                  </div>
                  
                  <div className="flex flex-wrap gap-1 mt-2">
                    {drama.tags.slice(0, 3).map((tag, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Empty State */}
        {filteredDramas.length === 0 && (
          <div className="text-center py-16">
            <i className="ri-film-line text-6xl text-gray-300 mb-4"></i>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No dramas found</h3>
            <p className="text-gray-600 mb-6">
              Try adjusting your filters or search terms to find more dramas.
            </p>
            <button
              onClick={clearFilters}
              className="px-6 py-3 bg-rose-600 hover:bg-rose-700 text-white rounded-lg transition-colors whitespace-nowrap cursor-pointer"
            >
              Clear All Filters
            </button>
          </div>
        )}

        {/* AI Dramas Gallery Section */}
        <div 
          ref={aiGalleryRef}
          className={`mt-16 transition-all duration-500 ${highlightGallery ? 'ring-4 ring-orange-500 ring-offset-4 ring-offset-gray-50 rounded-2xl' : ''}`}
        >
          <AIDramasGallery />
        </div>

        {/* AI Drama Creator Apply CTA */}
        <div className="mt-10 bg-gradient-to-r from-orange-500 via-rose-500 to-amber-500 rounded-2xl p-8 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/4"></div>
          <div className="absolute bottom-0 left-0 w-36 h-36 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/4"></div>
          <div className="relative flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                <i className="ri-robot-2-line text-3xl"></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-1">Want to Create AI Dramas?</h3>
                <p className="text-white/80 max-w-lg">
                  Apply to become a verified AI Drama Creator. Get featured placement, a creator badge, exclusive tools access, and connect with fellow creators.
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowAIDramaApply(true)}
              className="px-8 py-3.5 bg-white text-orange-600 font-bold rounded-xl hover:bg-orange-50 transition-colors cursor-pointer whitespace-nowrap shadow-xl text-lg"
            >
              <i className="ri-send-plane-line mr-2"></i>
              Apply Now
            </button>
          </div>
        </div>
      </div>

      {/* AI Drama Apply Modal */}
      <AIDramaApplyModal
        isOpen={showAIDramaApply}
        onClose={() => setShowAIDramaApply(false)}
      />

      <Footer />
    </div>
  );
};

export default DramasPage;
